import React from 'react';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logError, logger } from '@utils/usefulFuncs';

const ACTIONS = {
  init: 'init',
  reset: 'reset',
  update: 'u',
  error: 'error',
  complete: 'complete',
};

const initialState = {
  answersLookup: {},
  contextLookup: {},
  idToKeyLookup: {},
};

function createAnswersLookupFromCompletePayload(answers, state) {
  const l = {};
  (answers || []).forEach((row) => {
    if (!row) return;
    const idx = row.question_index;
    const key = row.template_key || (idx != null ? state.idToKeyLookup[idx] : null);
    if (!key) return;
    if (l[key] != null && !row.final_answer) return;
    l[key] = row.answer || '';
  });
  return l;
}

export function generateTemplateKeyToIdLookup(queryListMetadata) {
  const l = {};
  queryListMetadata.forEach((obj, i) => {
    // l[obj.template_key] = i;
    l[i] = obj.template_key;
  });
  return l;
}

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.reset: {
      return initialState;
    }

    case ACTIONS.init: {
      const { queryListMetadata } = action.payload;
      return {
        streamEnd: false,
        idToKeyLookup: generateTemplateKeyToIdLookup(queryListMetadata),
        answersLookup: {},
        contextLookup: {},
      };
    }

    case ACTIONS.update: {
      const { payload } = action;
      const streamData = typeof payload === 'string' ? JSON.parse(payload) : payload;
      const id = streamData?.question_index;
      const key = id != null ? state.idToKeyLookup[id] : null;
      if (!key) return state;
      return {
        ...state,
        error: null,
        answersLookup: {
          ...(state.answersLookup || {}),
          [key]: streamData.answer || '',
        },
      };
    }

    case ACTIONS.error: {
      return { ...state, error: action.payload };
    }

    case ACTIONS.complete: {
      const { payload } = action;
      // const contextLookup = createContextLookupFromCompletePayload(payload);
      return {
        ...state,
        streamEnd: true,
        error: null,
        answersLookup: createAnswersLookupFromCompletePayload(payload, state),
        // contextLookup,
      };
    }

    default:
      throw new Error('Missing case');
  }
}

async function getStreamingResponse(onCloseSource, resultId, dispatch) {
  return new Promise((resolve, reject) => {
    if (!resultId) {
      return reject('resultId was missing');
    }

    let source = createAuthedEventSource(
      `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true`
    );

    onCloseSource.call = () => {
      console.log('outside close source called');
      source.close(); // set outer ability to close stream if component unmounts
    };

    const onMessage = function (event) {
      try {
        const { data } = event;
        let parsed = null;
        try {
          parsed = JSON.parse(data);
        } catch {
          // Stream events can be non-JSON while the UI still appends the raw payload below.
        }
        logger('useDocumentTemplateAnswers onMessage', 'received', {
          runId: resultId,
          questionIndex: parsed?.question_index,
          finalAnswer: parsed?.final_answer === true,
          answerLength: parsed?.answer?.length || 0,
        });
        dispatch({
          type: ACTIONS.update,
          payload: data,
        });
      } catch (e) {
        logError('error caught in stream', e);
      }
    };
    const onOpen = function () {};
    const onError = function (e) {
      try {
        dispatch({
          type: ACTIONS.error,
          payload: 'Stream interrupted, retrying...',
        });
        logError('error received in stream', e);
        reject('error received in stream');
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };
    const onComplete = function (event) {
      try {
        const { data } = event;
        const streamData = JSON.parse(data);
        const answers = streamData?.response?.answers || [];
        dispatch({
          type: ACTIONS.complete,
          payload: answers,
        });

        source.removeEventListener('open', onOpen);
        source.removeEventListener('error', onError);
        source.removeEventListener('message', onMessage);
        source.close();
        resolve('complete');
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('message', onMessage);
    source.addEventListener('complete', onComplete);
  });
}

export const useDocumentTemplateAnswers = (resultId, queryListMetadata) => {
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  React.useEffect(() => {
    if (queryListMetadata) {
      dispatch({
        type: ACTIONS.init,
        payload: {
          queryListMetadata,
        },
      });
    } else {
      dispatch({
        type: ACTIONS.reset,
      });
    }
  }, [queryListMetadata]);

  React.useEffect(() => {
    const activeSources = [];
    const maxRetries = 2;
    const retryDelayMS = 1000;

    const getStreamingResponseWithRetries = (retriesLeft) => {
      const onCloseSource = {};
      activeSources.push(onCloseSource);

      getStreamingResponse(onCloseSource, resultId, dispatch).catch((error) => {
        if (retriesLeft > 0) {
          console.error(`Error occurred, retrying... Attempts left: ${retriesLeft}`);
          setTimeout(() => {
            getStreamingResponseWithRetries(retriesLeft - 1);
          }, retryDelayMS);
        }
      });
    };

    if (resultId) {
      getStreamingResponseWithRetries(maxRetries);
    }
    return () => {
      // Close all active sources
      activeSources.forEach((onCloseSource) => {
        onCloseSource?.call && onCloseSource?.call();
      });
      dispatch({
        type: ACTIONS.reset,
      });
    };
  }, [resultId]);

  return state;
};
